/**
 * Viewport Logic for Map Canvas
 * Handles strictly pure calculations for panning, zooming, and centering.
 */

export interface Point {
    x: number;
    y: number;
}

export interface Dimensions {
    width: number;
    height: number;
}

/**
 * Calculates the constrained pan coordinates to ensure the map remains correctly visible.
 * Enforces:
 * 1. If map is smaller than container -> Center it exactly.
 * 2. If map is larger than container -> Clamp edges to container bounds (no flying away).
 *
 * @param currentPan - The current or proposed pan vector
 * @param zoom - Current zoom level
 * @param container - Dimensions of the viewport container
 * @param renderWidth - Base width of the map content
 * @param renderHeight - Base height of the map content
 */
export function calculateClampedPan(
    currentPan: Point,
    zoom: number,
    container: Dimensions,
    renderWidth: number,
    renderHeight: number,
): Point {
    const { width, height } = container;

    // Safety check for unsized container
    if (width <= 0 || height <= 0) return { x: 0, y: 0 };

    const logicalWidth = renderWidth * zoom;
    const logicalHeight = renderHeight * zoom;

    let nx = currentPan.x;
    let ny = currentPan.y;

    // Horizontally
    if (logicalWidth <= width) {
        // CONTENT SMALLER THAN CONTAINER: Force Center
        nx = (width - logicalWidth) / 2;
    } else {
        // CONTENT LARGER: Clamp
        // Min pan (scrolled all the way right) = width - logicalWidth
        // Max pan (scrolled all the way left) = 0
        const minX = width - logicalWidth;
        const maxX = 0;
        nx = Math.min(maxX, Math.max(nx, minX));
    }

    // Vertically
    if (logicalHeight <= height) {
        // CONTENT SMALLER: Force Center
        ny = (height - logicalHeight) / 2;
    } else {
        // CONTENT LARGER: Clamp
        const minY = height - logicalHeight;
        const maxY = 0;
        ny = Math.min(maxY, Math.max(ny, minY));
    }

    return { x: nx, y: ny };
}

/**
 * Helper to determine if the standard centering rules apply.
 * Returns true if the state allows for calculation (valid dimensions).
 */
export function isValidDimensions(d: Dimensions): boolean {
    return d.width > 0 && d.height > 0;
}
